import { message } from '../utils/antdMessage';
import useOrthoTaskList from './useOrthoTaskList';
import useOrthoKindOverlays from './useOrthoKindOverlays';
import useOrthoPhotoDensity from './useOrthoPhotoDensity';
import useOrthoModelingActions from './useOrthoModelingActions';

/**
 * 航拍建模任务：组合子 hooks，对外保持原 useOrthoTasks 公共 API
 */
export default function useOrthoTasks({
  canManage = false,
  canApprove = false,
  canViewLayers = false,
  getMap,
  mapReady,
  onStartInteract,
  holdPanelOpenRef,
  expanded,
  setExpanded,
  setPinned,
  pinForSecondaryMenu,
  // bbox bridge (stable refs recommended)
  beginBboxSelect,
  clearBoxPreview,
  setSelectMode,
  beginAoiInteract,
  endAoiInteractSoon,
  setBlockEarthBlankFly,
  isMobile = false
}) {
  const list = useOrthoTaskList({
    canManage,
    canApprove,
    canViewLayers,
    holdPanelOpenRef,
    setExpanded,
    setPinned
  });

  const overlays = useOrthoKindOverlays({
    getMap,
    onStartInteract,
    expanded,
    setExpanded,
    setPinned,
    pinForSecondaryMenu,
    setBusy: list.setBusy,
    layers: list.layers,
    loadLayers: list.loadLayers,
    isMobile
  });

  const density = useOrthoPhotoDensity({
    getMap,
    mapReady,
    onStartInteract,
    setBusy: list.setBusy,
    setOrthoManageOpen: list.setOrthoManageOpen,
    setGlbMapActive: overlays.setGlbMapActive,
    setBlockEarthBlankFly,
    beginBboxSelect,
    clearBoxPreview,
    setSelectMode,
    beginAoiInteract,
    endAoiInteractSoon,
    activeId: list.activeId,
    activeTask: list.activeTask,
    loadTasks: list.loadTasks
  });

  const actions = useOrthoModelingActions({
    activeId: list.activeId,
    activeTask: list.activeTask,
    setBusy: list.setBusy,
    code: list.code,
    setCode: list.setCode,
    wantGlb: list.wantGlb,
    purgeInboxOnSuccess: list.purgeInboxOnSuccess,
    setRevealedCode: list.setRevealedCode,
    loadTasks: list.loadTasks,
    loadPending: list.loadPending,
    loadLayers: list.loadLayers,
    refreshStatusBar: list.refreshStatusBar,
    photoRenderBoundsRef: density.photoRenderBoundsRef,
    clearPhotoDensityLayer: density.clearPhotoDensityLayer,
    openPhotoPreview: density.openPhotoPreview
  });

  const openOrthoManage = () => {
    if (!canManage) {
      message.warning('需要「航拍建模」权限');
      return;
    }
    overlays.setOrthoKindMenuOpen({ '2d': false, '3d': false });
    list.setOrthoManageOpen(true);
    setPinned(true);
    setExpanded(true);
    list.loadTasks();
    list.loadLayers();
  };

  return {
    tasks: list.tasks,
    setTasks: list.setTasks,
    pending: list.pending,
    setPending: list.setPending,
    activeId: list.activeId,
    setActiveId: list.setActiveId,
    activeTask: list.activeTask,
    title: list.title,
    setTitle: list.setTitle,
    code: list.code,
    setCode: list.setCode,
    wantGlb: list.wantGlb,
    setWantGlb: list.setWantGlb,
    purgeInboxOnSuccess: list.purgeInboxOnSuccess,
    setPurgeInboxOnSuccess: list.setPurgeInboxOnSuccess,
    busy: list.busy,
    setBusy: list.setBusy,
    layers: list.layers,
    setLayers: list.setLayers,
    revealedCode: list.revealedCode,
    setRevealedCode: list.setRevealedCode,
    orthoUploadText: list.orthoUploadText,
    setOrthoUploadText: list.setOrthoUploadText,
    orthoPhotoRef: list.orthoPhotoRef,
    orthoFolderRef: list.orthoFolderRef,
    glbViewer: overlays.glbViewer,
    setGlbViewer: overlays.setGlbViewer,
    assetsModalOpen: list.assetsModalOpen,
    setAssetsModalOpen: list.setAssetsModalOpen,
    glbMapActive: overlays.glbMapActive,
    setGlbMapActive: overlays.setGlbMapActive,
    glbLoading: overlays.glbLoading,
    orthoKindChecked: overlays.orthoKindChecked,
    setOrthoKindChecked: overlays.setOrthoKindChecked,
    orthoKindMenuOpen: overlays.orthoKindMenuOpen,
    setOrthoKindMenuOpen: overlays.setOrthoKindMenuOpen,
    orthoManageOpen: list.orthoManageOpen,
    setOrthoManageOpen: list.setOrthoManageOpen,
    ortho2dActive: overlays.ortho2dActive,
    setOrtho2dActive: overlays.setOrtho2dActive,
    photoRenderBoundsRef: density.photoRenderBoundsRef,
    refreshStatusBar: list.refreshStatusBar,
    loadTasks: list.loadTasks,
    loadPending: list.loadPending,
    loadLayers: list.loadLayers,
    createTask: list.createTask,
    uploadOrthoPhotos: list.uploadOrthoPhotos,
    markReady: actions.markReady,
    unreadyTask: actions.unreadyTask,
    withdrawRequest: actions.withdrawRequest,
    deleteTask: list.deleteTask,
    clearPhotoDensityLayer: density.clearPhotoDensityLayer,
    prepareFlatMapForPhotoFly: density.prepareFlatMapForPhotoFly,
    ensureMapVisibleForSelect: density.ensureMapVisibleForSelect,
    flyToPhotoRenderArea: density.flyToPhotoRenderArea,
    paintPhotoDensity: density.paintPhotoDensity,
    startRenderAoiSelect: density.startRenderAoiSelect,
    openPhotoPreview: density.openPhotoPreview,
    prepareRemodel: actions.prepareRemodel,
    clearTaskAssets: actions.clearTaskAssets,
    abortModeling: actions.abortModeling,
    requestStart: actions.requestStart,
    confirmCode: actions.confirmCode,
    approveTask: actions.approveTask,
    revealCode: actions.revealCode,
    rejectTask: actions.rejectTask,
    openGlbViewer: overlays.openGlbViewer,
    attachGlbToLayer: overlays.attachGlbToLayer,
    poseEditor: overlays.poseEditor,
    poseSaving: overlays.poseSaving,
    openPoseEditor: overlays.openPoseEditor,
    previewModelPose: overlays.previewModelPose,
    saveModelPose: overlays.saveModelPose,
    closePoseEditor: overlays.closePoseEditor,
    toggleGlbOnMap: overlays.toggleGlbOnMap,
    toggleLayerOnMap: overlays.toggleLayerOnMap,
    flyToOrthoLayer: overlays.flyToOrthoLayer,
    flyToOrtho3d: overlays.flyToOrtho3d,
    ensureOrtho2dOn: overlays.ensureOrtho2dOn,
    ensureOrtho2dOff: overlays.ensureOrtho2dOff,
    ensureOrtho3dOn: overlays.ensureOrtho3dOn,
    ensureOrtho3dOff: overlays.ensureOrtho3dOff,
    turnOffOrthoKindOverlays: overlays.turnOffOrthoKindOverlays,
    openOrthoKindMenu: overlays.openOrthoKindMenu,
    onOrthoKindLabelOpenChange: overlays.onOrthoKindLabelOpenChange,
    onOrthoKindMasterCheck: overlays.onOrthoKindMasterCheck,
    onOrthoItemCheck: overlays.onOrthoItemCheck,
    openOrthoManage,
    confirmOrthoLoadBbox: overlays.confirmOrthoLoadBbox,
    confirmRenderAoiBbox: density.confirmRenderAoiBbox
  };
}
